import { useState, useEffect } from 'react';
import { 
  FiFileText, 
  FiCalendar, 
  FiServer, 
  FiMonitor,
  FiActivity,
  FiDownload,
  FiPrinter,
  FiTrendingUp
} from 'react-icons/fi';
import api from '../services/api';
import { toast } from 'react-toastify';
import { formatDate } from '../utils/dateFormat';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';

const Reports = () => {
  const [loading, setLoading] = useState(false);
  const [selectedReport, setSelectedReport] = useState('exam-by-time');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    fetchReportData();
  }, [selectedReport, dateRange]);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const params = {
        start_date: dateRange.startDate,
        end_date: dateRange.endDate,
        period: 'all'
      };

      const response = await api.get('/stats', { params });
      if (response.data.success && response.data.data) {
        setReportData(response.data.data);
      }
    } catch (error) {
      console.error('Fetch report error:', error);
      toast.error('Không thể tải dữ liệu báo cáo');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = (format = 'csv') => {
    toast.info(`Tính năng xuất ${format.toUpperCase()} đang được phát triển`);
  };

  const handlePrint = () => {
    window.print();
  };

  const reportTypes = [
    { id: 'exam-by-time', label: 'Báo cáo ca chụp theo thời gian', icon: FiCalendar },
    { id: 'exam-by-site', label: 'Báo cáo theo cơ sở', icon: FiServer },
    { id: 'exam-by-station', label: 'Báo cáo theo máy CLS', icon: FiMonitor },
    { id: 'exam-by-modality', label: 'Báo cáo theo loại máy', icon: FiActivity },
    { id: 'exam-by-status', label: 'Báo cáo theo trạng thái', icon: FiTrendingUp },
    { id: 'worklist-report', label: 'Báo cáo Worklist', icon: FiFileText },
  ];

  const renderReportContent = () => {
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[200px] gap-3">
          <div className="w-7 h-7 border-2 border-slate-200 border-t-primary rounded-full animate-spin"></div>
          <p className="text-slate-500 text-sm">Đang tải dữ liệu báo cáo...</p>
        </div>
      );
    }

    if (!reportData) {
      return <div className="text-center p-6 text-slate-500 text-sm">Không có dữ liệu báo cáo</div>;
    }

    switch (selectedReport) {
      case 'exam-by-time':
        return renderExamByTimeReport();
      case 'exam-by-site':
        return renderExamBySiteReport();
      case 'exam-by-station':
        return renderExamByStationReport();
      case 'exam-by-modality':
        return renderExamByModalityReport();
      case 'exam-by-status':
        return renderExamByStatusReport();
      case 'worklist-report':
        return renderWorklistReport();
      default:
        return <div>Chọn loại báo cáo</div>;
    }
  };

  const renderExamByTimeReport = () => {
    const dailyData = reportData.daily || [];
    
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">Tổng số ca chụp</h4>
            <p className="text-2xl font-bold text-slate-900">{reportData.total || 0}</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">Tỷ lệ hoàn thành</h4>
            <p className="text-2xl font-bold text-emerald-600">{reportData.completionRate || 0}%</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">Khoảng thời gian</h4>
            <p className="text-sm font-medium text-slate-900">
              {formatDate(dateRange.startDate)} - {formatDate(dateRange.endDate)}
            </p>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm">
          <div className="flex items-center justify-between mb-3 pb-2 border-b border-slate-200">
            <h3 className="text-sm font-semibold text-slate-900">Biểu đồ ca chụp theo ngày</h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 11 }}
                tickFormatter={(value) => formatDate(value)}
              />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip 
                labelFormatter={(value) => formatDate(value)}
                contentStyle={{ fontSize: '0.75rem' }}
              />
              <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
              <Line type="monotone" dataKey="count" stroke="#2563eb" strokeWidth={2} name="Số ca chụp" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-900 mb-3 pb-2 border-b border-slate-200">Chi tiết theo ngày</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-100 text-slate-700">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Ngày</th>
                  <th className="px-3 py-2 text-left font-semibold">Số ca chụp</th>
                </tr>
              </thead>
              <tbody>
                {dailyData.map((item, index) => (
                  <tr key={index} className="gpacs-table-row border-t border-slate-200 hover:bg-slate-100 transition-colors">
                    <td className="px-3 py-2">{formatDate(item.date)}</td>
                    <td className="px-3 py-2">{item.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderExamBySiteReport = () => {
    const siteData = reportData.bySite || [];
    const COLORS = ['#2563eb', '#059669', '#d97706', '#dc2626', '#0284c7', '#8b5cf6'];

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">Tổng số cơ sở</h4>
            <p className="text-2xl font-bold text-slate-900">{reportData.totalSites || 0}</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">Tổng số ca chụp</h4>
            <p className="text-2xl font-bold text-slate-900">{reportData.total || 0}</p>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm">
          <div className="flex items-center justify-between mb-3 pb-2 border-b border-slate-200">
            <h3 className="text-sm font-semibold text-slate-900">Phân bố ca chụp theo cơ sở</h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={siteData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="site" 
                tick={{ fontSize: 11 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ fontSize: '0.75rem' }} />
              <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
              <Bar dataKey="count" fill="#2563eb" name="Số ca chụp" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-900 mb-3 pb-2 border-b border-slate-200">Chi tiết theo cơ sở</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-100 text-slate-700">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Cơ sở</th>
                  <th className="px-3 py-2 text-left font-semibold">Số ca chụp</th>
                  <th className="px-3 py-2 text-left font-semibold">Tỷ lệ</th>
                </tr>
              </thead>
              <tbody>
                {siteData.map((item, index) => (
                  <tr key={index} className="gpacs-table-row border-t border-slate-200 hover:bg-slate-100 transition-colors">
                    <td className="px-3 py-2">{item.site}</td>
                    <td className="px-3 py-2">{item.count}</td>
                    <td className="px-3 py-2">
                      {reportData.total > 0 
                        ? ((item.count / reportData.total) * 100).toFixed(1) 
                        : 0}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderExamByStationReport = () => {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">Tổng số máy CLS</h4>
            <p className="text-2xl font-bold text-slate-900">{reportData.totalStations || 0}</p>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center min-h-[120px] gap-2 text-slate-500">
          <FiFileText className="text-4xl text-slate-300" />
          <p>Dữ liệu báo cáo theo máy CLS đang được phát triển</p>
        </div>
      </div>
    );
  };

  const renderExamByModalityReport = () => {
    const modalityData = reportData.byModality || [];
    const COLORS = ['#2563eb', '#059669', '#d97706', '#dc2626', '#0284c7', '#8b5cf6'];

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">Tổng số loại máy</h4>
            <p className="text-2xl font-bold text-slate-900">{modalityData.length}</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">Tổng số ca chụp</h4>
            <p className="text-2xl font-bold text-slate-900">{reportData.total || 0}</p>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm">
          <div className="flex items-center justify-between mb-3 pb-2 border-b border-slate-200">
            <h3 className="text-sm font-semibold text-slate-900">Phân bố ca chụp theo loại máy</h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={modalityData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ modality, count, percent }) => `${modality}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="count"
              >
                {modalityData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ fontSize: '0.75rem' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-900 mb-3 pb-2 border-b border-slate-200">Chi tiết theo loại máy</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-100 text-slate-700">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Loại máy</th>
                  <th className="px-3 py-2 text-left font-semibold">Số ca chụp</th>
                  <th className="px-3 py-2 text-left font-semibold">Tỷ lệ</th>
                </tr>
              </thead>
              <tbody>
                {modalityData.map((item, index) => (
                  <tr key={index} className="gpacs-table-row border-t border-slate-200 hover:bg-slate-100 transition-colors">
                    <td className="px-3 py-2">{item.modality}</td>
                    <td className="px-3 py-2">{item.count}</td>
                    <td className="px-3 py-2">
                      {reportData.total > 0 
                        ? ((item.count / reportData.total) * 100).toFixed(1) 
                        : 0}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderExamByStatusReport = () => {
    const statusData = reportData.byStatus || {};
    const statusArray = Object.entries(statusData).map(([status, count]) => ({
      status: getStatusLabel(status),
      count
    }));

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">Tổng số ca chụp</h4>
            <p className="text-2xl font-bold text-slate-900">{reportData.total || 0}</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">Tỷ lệ hoàn thành</h4>
            <p className="text-2xl font-bold text-emerald-600">{reportData.completionRate || 0}%</p>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm">
          <div className="flex items-center justify-between mb-3 pb-2 border-b border-slate-200">
            <h3 className="text-sm font-semibold text-slate-900">Phân bố ca chụp theo trạng thái</h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={statusArray}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="status" 
                tick={{ fontSize: 11 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ fontSize: '0.75rem' }} />
              <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
              <Bar dataKey="count" fill="#2563eb" name="Số ca chụp" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-900 mb-3 pb-2 border-b border-slate-200">Chi tiết theo trạng thái</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-100 text-slate-700">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Trạng thái</th>
                  <th className="px-3 py-2 text-left font-semibold">Số ca chụp</th>
                  <th className="px-3 py-2 text-left font-semibold">Tỷ lệ</th>
                </tr>
              </thead>
              <tbody>
                {statusArray.map((item, index) => (
                  <tr key={index} className="gpacs-table-row border-t border-slate-200 hover:bg-slate-100 transition-colors">
                    <td className="px-3 py-2">{item.status}</td>
                    <td className="px-3 py-2">{item.count}</td>
                    <td className="px-3 py-2">
                      {reportData.total > 0 
                        ? ((item.count / reportData.total) * 100).toFixed(1) 
                        : 0}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderWorklistReport = () => {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">Tổng số Worklist</h4>
            <p className="text-2xl font-bold text-slate-900">{reportData.totalWorklists || 0}</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">Tổng số ca chụp</h4>
            <p className="text-2xl font-bold text-slate-900">{reportData.total || 0}</p>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center min-h-[120px] gap-2 text-slate-500">
          <FiFileText className="text-4xl text-slate-300" />
          <p>Chi tiết báo cáo Worklist đang được phát triển</p>
        </div>
      </div>
    );
  };

  const getStatusLabel = (status) => {
    const labels = {
      scheduled: 'Đã lên lịch',
      in_progress: 'Đang thực hiện',
      completed: 'Hoàn thành',
      cancelled: 'Đã hủy',
      synced: 'Đã đồng bộ'
    };
    return labels[status] || status;
  };

  return (
    <div className="animate-fade-in">
      <div className="mb-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex flex-col gap-1 min-w-[220px]">
              <label className="text-xs font-medium text-slate-700">Loại báo cáo</label>
              <select 
                className="w-full px-3 py-2 border border-slate-300 rounded bg-white text-slate-800 text-sm focus:outline-none focus:border-primary" 
                value={selectedReport}
                onChange={(e) => setSelectedReport(e.target.value)}
              >
                {reportTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.label}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1 min-w-[180px]">
              <label className="text-xs font-medium text-slate-700">Từ ngày</label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-slate-300 rounded bg-white text-slate-800 text-sm focus:outline-none focus:border-primary"
                value={dateRange.startDate}
                onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1 min-w-[180px]">
              <label className="text-xs font-medium text-slate-700">Đến ngày</label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-slate-300 rounded bg-white text-slate-800 text-sm focus:outline-none focus:border-primary"
                value={dateRange.endDate}
                onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button className="inline-flex h-9 items-center gap-2 rounded-md bg-slate-100 px-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-200" onClick={handlePrint}>
              <FiPrinter className="text-base" />
              In báo cáo
            </button>
            <button className="inline-flex h-9 items-center gap-2 rounded-md bg-slate-100 px-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-200" onClick={() => handleExport('csv')}>
              <FiDownload className="text-base" />
              Xuất CSV
            </button>
            <button className="inline-flex h-9 items-center gap-2 rounded-md bg-slate-100 px-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-200" onClick={() => handleExport('excel')}>
              <FiDownload className="text-base" />
              Xuất Excel
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {reportTypes.map(type => {
          const Icon = type.icon;
          const active = selectedReport === type.id;
          return (
            <button
              key={type.id}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded border transition-colors ${
                active ? 'bg-primary text-white border-primary' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
              }`}
              onClick={() => setSelectedReport(type.id)}
            >
              <Icon className="text-base" />
              <span>{type.label}</span>
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
        {renderReportContent()}
      </div>
    </div>
  );
};

export default Reports;


